import React, { useState, useEffect, useRef } from 'react';
import { Search, Calendar, ChevronDown, Check, X, Filter } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import '../styles/notifications.scss';
import Layout from "../components/Layout"
import Loader from "../components/Loader";
import { api, getAuthHeaders } from '../utils/api';

const Notifications = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Notifications state from API
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalCountUnread, setTotalCountUnread] = useState(0);
  const [allNotificationsCount, setAllNotificationsCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Refs for click outside handling
  const typeDropdownRef = useRef(null);
  const datePickerRef = useRef(null);

  // ML-37: only the latest request may update the list. Without this, a slow "All" response
  // that lands after the user switches to Read overwrites the Read results.
  const latestRequestId = useRef(0);

  // Fetch notifications function for reuse
  const fetchNotifications = async () => {
    const requestId = ++latestRequestId.current;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      if (searchTerm) params.append('search', searchTerm);
      if (selectedType !== 'all') params.append('priority', selectedType.toLowerCase());
      if (startDate) params.append('dateFrom', startDate.toISOString().split('T')[0]);
      if (endDate) params.append('dateTo', endDate.toISOString().split('T')[0]);
      if (activeFilter === 'read') params.append('readStatus', 'READ');
      if (activeFilter === 'unread') params.append('readStatus', 'UNREAD');

      const response = await api.get(`/notifications?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      if (requestId !== latestRequestId.current) return;
      setNotifications(response.data.results || []);
      setTotal(response.data.totalResults || response.data.total || 0);
      // Only update allNotificationsCount when fetching all notifications (no filter)
      if (activeFilter === 'all' && !searchTerm && selectedType === 'all' && !startDate && !endDate) {
        setAllNotificationsCount(response.data.totalResults || response.data.total || 0);
      }
      // Get unread count from analytics endpoint
      try {
        const analyticsRes = await api.get('/notifications/analytics/unread-count', { headers: getAuthHeaders() });
        setTotalCountUnread(analyticsRes.data.unreadCount || 0);
      } catch (e) {
        setTotalCountUnread(response.data.unreadCount || 0);
      }
      setTotalPages(response.data.totalPages || Math.ceil((response.data.totalResults || response.data.total || 0) / limit) || 1);
    } catch (err) {
      if (requestId === latestRequestId.current) setError('Failed to load notifications.');
    } finally {
      if (requestId === latestRequestId.current) setLoading(false);
    }
  };

  // Switching tabs starts again from the first page of that tab
  const selectFilter = (filter) => {
    setActiveFilter(filter);
    setPage(1);
  };

  // Debounce search
  const searchTimeout = useRef();

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (searchTerm && searchTerm.trim().length > 0 && searchTerm.trim().length < 3) {
      // Don't call API for short search
      return;
    }
    searchTimeout.current = setTimeout(() => {
      fetchNotifications();
    }, 500);
    return () => clearTimeout(searchTimeout.current);
    // eslint-disable-next-line
  }, [page, limit, searchTerm, selectedType, startDate, endDate, activeFilter]);

  // Fetch initial counts on component mount
  useEffect(() => {
    const fetchInitialCounts = async () => {
      try {
        // Get total all notifications count
        const allResponse = await api.get('/notifications?page=1&limit=1', {
          headers: getAuthHeaders(),
        });
        setAllNotificationsCount(allResponse.data.totalResults || allResponse.data.total || 0);
        
        // Get unread count from analytics
        const analyticsRes = await api.get('/notifications/analytics/unread-count', {
          headers: getAuthHeaders(),
        });
        setTotalCountUnread(analyticsRes.data.unreadCount || 0);
      } catch (e) {
        console.error('Failed to fetch initial counts', e);
      }
    };
    fetchInitialCounts();
  }, []);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target)) {
        setShowTypeDropdown(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getNotificationIcon = (priority) => {
    const iconMap = {
      urgent: '!',
      high: '!',
      low: 'i',
      medium: '✓'
    };
    return iconMap[priority] || 'i';
  };

  // All filtering is now handled by backend, so just use notifications as is
  const filteredNotifications = notifications;

  // Format notification time: 'x hr. ago' if <24h, else DD-MM-YYYY
  const formatNotificationTime = (updatedAt) => {
    if (!updatedAt) return '';
    const updatedDate = new Date(updatedAt);
    const now = new Date();
    const diffMs = now - updatedDate;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays >= 1) {
      // Format as DD-MM-YYYY
      const day = String(updatedDate.getDate()).padStart(2, '0');
      const month = String(updatedDate.getMonth() + 1).padStart(2, '0');
      const year = updatedDate.getFullYear();
      return `${day}-${month}-${year}`;
    } else if (diffHours >= 1) {
      return `${diffHours} hr. ago`;
    } else {
      return `${diffMinutes} min. ago`;
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`, {}, { headers: getAuthHeaders() });
      await fetchNotifications();
      // Dispatch event to update Header's bell icon count
      window.dispatchEvent(new Event('notificationsChanged'));
    } catch (e) {
      // Optionally show error toast
    }
  };

  const markAllAsRead = async () => {
    try {
      // Collect all unread notification IDs
      const unreadIds = notifications.filter(n => !n.read).map(n => n._id);
      if (unreadIds.length === 0) return;
      // Call bulk read API (assuming PUT /notifications/actions/read-all with { ids: [...] })
      await api.put('/notifications/actions/read-all', {}, { headers: getAuthHeaders() });
      await fetchNotifications();
      // Dispatch event to update Header's bell icon count
      window.dispatchEvent(new Event('notificationsChanged'));
    } catch (e) {
      // Optionally show error toast
    }
  };

  const clearNotification = (id) => {
    setNotifications(prevNotifications =>
      prevNotifications.filter(notification => notification.id !== id)
    );
  };

  const clearDateFilter = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const clearAllFilters = () => {
    setSelectedType('all');
    setStartDate(null);
    setEndDate(null);
    setSearchTerm('');
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  const totalCount = total;

  const typeOptions = [
    { value: 'all', label: 'Type', color: '' },
    { value: 'urgent', label: 'Critical', color: '#ef4444' },
    { value: 'high', label: 'Warning', color: '#f59e0b' },
    { value: 'low', label: 'General', color: '#3b82f6' },
    { value: 'medium', label: 'Done', color: '#10b981' }
  ];

  const hasActiveFilters = selectedType !== 'all' || startDate || endDate;

  // totalPages now comes from backend or fallback calculation

  return (
    <Layout>
    <div className="notification-page">
  {loading && <Loader />}
      {error && (
        <div className="notification-error">{error}</div>
      )}
       <div className="notification-filters d-flex justify-content-between">
        <div className="filter-tabs">
          <button 
            className={`filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => selectFilter('all')}
          >
            All <span className="tab-count">{allNotificationsCount}</span>
          </button>
          <button 
            className={`filter-tab ${activeFilter === 'read' ? 'active' : ''}`}
            onClick={() => selectFilter('read')}
          >
            Read
          </button>
          <button 
            className={`filter-tab ${activeFilter === 'unread' ? 'active' : ''}`}
            onClick={() => selectFilter('unread')}
          >
            Unread <span className="tab-count unread-count">{totalCountUnread}</span>
          </button>
        </div>

        <div className={`filter-controls ${totalCountUnread === 0 ? 'no-mark-btn' : ''}`}>
          <div className="search-container">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Search..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="date-filter-container" ref={datePickerRef}>
            <button 
              className="date-filter-btn"
              onClick={() => setShowDatePicker(!showDatePicker)}
            >
              <Calendar className="filter-icon" />
            </button>
            {showDatePicker && (
              <div className="date-picker-dropdown">
                <div className="date-picker-header">
                  <span>Date Range</span>
                  {(startDate || endDate) && (
                    <button className="clear-date-btn" onClick={clearDateFilter}>
                      Clear
                    </button>
                  )}
                </div>
                <div className="date-picker-content">
                  <div className="date-input-group">
                    <label>From:</label>
                    <DatePicker
                      selected={startDate}
                      onChange={setStartDate}
                      selectsStart
                      startDate={startDate}
                      endDate={endDate}
                      placeholderText="Start date"
                      className="date-input"
                    />
                  </div>
                  <div className="date-input-group">
                    <label>To:</label>
                    <DatePicker
                      selected={endDate}
                      onChange={setEndDate}
                      selectsEnd
                      startDate={startDate}
                      endDate={endDate}
                      minDate={startDate}
                      placeholderText="End date"
                      className="date-input"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="dropdown-not-container" ref={typeDropdownRef}>
            <button 
              className="dropdown-not-btn"
              onClick={() => setShowTypeDropdown(!showTypeDropdown)}
            >
              {typeOptions.find(opt => opt.value === selectedType)?.label || 'Type'}
              <ChevronDown className="dropdown-not-arrow" />
            </button>
            {showTypeDropdown && (
              <div className="dropdown-not-menu">
                {typeOptions.map(option => (
                  <button
                    key={option.value}
                    className="dropdown-not-item"
                    onClick={() => {
                      setSelectedType(option.value);
                      setShowTypeDropdown(false);
                    }}
                  >
                    {option.color && (
                      <span 
                        className="type-color-indicator"
                        style={{ backgroundColor: option.color }}
                      ></span>
                    )}
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {totalCountUnread > 0 && (
            <button className="mark-all-read-page-btn" onClick={markAllAsRead}>
              <Check className="mark-all-icon" />
              Mark all as read
            </button>
          )}
        </div>
      </div>

      {hasActiveFilters && (
        <div className="active-filters">
          {selectedType !== 'all' && (
            <span className="filter-chip">
              {selectedType}
              <button onClick={() => setSelectedType('all')}>
                <X className="chip-close" />
              </button>
            </span>
          )}
          {(startDate || endDate) && (
            <span className="filter-chip">
              {startDate ? startDate.toLocaleDateString() : 'Start'} - {endDate ? endDate.toLocaleDateString() : 'End'}
              <button onClick={clearDateFilter}>
                <X className="chip-close" />
              </button>
            </span>
          )}
          <button className="reset-filters" onClick={clearAllFilters}>
            Reset all filters
          </button>
        </div>
      )}

  <div className="notification-list">
        {filteredNotifications.length === 0 ? (
          <div className="no-notifications">
            <div className="no-notifications-icon">
              <Filter className="filter-empty-icon" />
            </div>
            <h3>No notifications found</h3>
            <p>Try adjusting your filters or search terms</p>
          </div>
        ) : (
          <>
            {filteredNotifications.map((notification) => (
              <div 
                key={notification.id} 
                className={`notification-list-item ${!notification.read ? 'unread' : ''}`}
              >
                <div className="notification-item-content">
                  <div className={`notification-icon ${notification.priority}`}>
                    {getNotificationIcon(notification.priority)}
                    {!notification.read && <div className="unread-dot"></div>}
                  </div>
                  <div className="notification-details">
                    <p className={`notification-message ${!notification.read ? 'unread-text' : ''}`}>
                      {notification.text}
                    </p>
                    {/* Show when the notification was created; updatedAt changes when it is marked read (ML-37) */}
                    <span className="notification-time">{formatNotificationTime(notification.createdAt || notification.updatedAt)}</span>
                  </div>
                </div>
                <div className="notification-actions">
                  {!notification.read && (
                    <button 
                      className="action-not-btn mark-read-action"
                      onClick={() => markAsRead(notification._id)}
                      title="Mark as read"
                    >
                      <Check className="action-not-icon" />
                      Mark as read
                    </button>
                  )}
                  {/* <button 
                    className="action-not-btn clear-action"
                    onClick={() => clearNotification(notification.id)}
                    title="Clear notification"
                  >
                    <X className="action-not-icon" />
                  </button> */}
                </div>
              </div>
            ))}
            {/* Pagination Controls - Styled like MyLiability */}
            {!loading && notifications.length > 0 && (
              <div className="pagination-controls" style={{ display: 'flex', justifyContent: 'center', margin: '24px 0', gap: 8 }}>
                <button
                  className="btn-sm pagination-text"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  Previous
                </button>
                {(() => {
                  const start = Math.max(1, page - 1);
                  const end = Math.min(totalPages, page + 1);
                  return Array.from({ length: end - start + 1 }, (_, i) => start + i).map((pg) => (
                    <button
                      key={pg}
                      className={`btn btn-sm paginations-list ${pg === page ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setPage(pg)}
                      disabled={pg === page}
                    >
                      {pg}
                    </button>
                  ));
                })()}
                <button
                  className="btn-sm pagination-text"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                >
                  Next
                </button>
                <span style={{ alignSelf: 'center', fontWeight: 500, marginLeft: 12 }}>
                  of {totalPages} pages
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </Layout>
  );
};

export default Notifications;